import React, { useState } from "react";
import { SectionToolbar, AddButton } from "./ProfileUI";
import { useParams } from "react-router-dom";
import {
  useEmploymentHistory,
  useSaveEmploymentHistory,
  useDeleteEmploymentHistory,
} from "../../../api/queries/profileQueries";
import { useCheckCompleteness } from "../../../api/queries/employeeQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import "bootstrap-icons/font/bootstrap-icons.css";

const EMPTY_FORM = {
  companyName: "",
  companyAddress: "",
  employeeType: "Permanent",
  position: "",
  startDate: "",
  endDate: "",
  reasonForLeaving: "",
};

const EmployeeHistory = () => {
  const { id: employeeId } = useParams();

  const { data: histories = [] } = useEmploymentHistory(employeeId);
  const saveHistory = useSaveEmploymentHistory(employeeId);
  const deleteHistory = useDeleteEmploymentHistory(employeeId);
  const checkCompleteness = useCheckCompleteness();

  const [formData, setFormData] = useState({ ...EMPTY_FORM });
  const [editingId, setEditingId] = useState(null);
  const [statusMsg, setStatusMsg] = useState({ text: "", type: "" });
  const [isProcessing, setIsProcessing] = useState(false);

  const employeeTypes = [
    { label: "Permanent", value: "Permanent" },
    { label: "Probationary", value: "Probationary" },
    { label: "Full-time", value: "Full-time" },
    { label: "Part-time", value: "Part-time" },
    { label: "Contract", value: "Contract" },
    { label: "Temporary", value: "Temporary" },
    { label: "Seasonal", value: "Seasonal" },
    { label: "On-call", value: "On-call" },
    { label: "Intern", value: "Intern" },
    { label: "Freelancer", value: "Freelancer" },
  ];

  const showStatus = (text, type = "success") => {
    setStatusMsg({ text, type });
    setTimeout(() => setStatusMsg({ text: "", type: "" }), 4000);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (statusMsg.text) setStatusMsg({ text: "", type: "" });
  };

  const handleEditInitiate = (item) => {
    setEditingId(item.id);
    setFormData({
      companyName: item.companyName || "",
      companyAddress: item.companyAddress || "",
      employeeType: item.employeeType || "",
      position: item.position || "",
      startDate: item.startDate?.slice(0, 10) || "",
      endDate: item.endDate?.slice(0, 10) || "",
      reasonForLeaving: item.reasonForLeaving || "",
    });
  };

  const handleCancel = () => {
    setEditingId(null);
    setFormData({ ...EMPTY_FORM });
    setStatusMsg({ text: "", type: "" });
  };

  const handleSave = async (id) => {
    if (!formData.companyName || !formData.position || !formData.startDate) {
      showStatus("Required fields missing.", "danger");
      return;
    }

    const start = new Date(formData.startDate);
    const end = formData.endDate ? new Date(formData.endDate) : null;
    if (end && start > end) {
      showStatus("Check date sequence.", "danger");
      return;
    }

    setIsProcessing(true);
    try {
      const payload = { ...formData, employeeId };

      // The list refreshes automatically after save
      const isUpdate = id && id !== "new";
      await saveHistory.mutateAsync({
        id: isUpdate ? id : null,
        data: payload,
      });
      showStatus(isUpdate ? "Updated successfully!" : "Added successfully!");

      // Recalculates Complete/Incomplete and refreshes the employee record
      await checkCompleteness.mutateAsync(employeeId);

      setEditingId(null);
      setFormData({ ...EMPTY_FORM });
    } catch (err) {
      showStatus(getApiErrorMessage(err, "Operation failed."), "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this record?")) return;

    setIsProcessing(true);
    try {
      await deleteHistory.mutateAsync(id);
      await checkCompleteness.mutateAsync(employeeId);
      showStatus("Deleted successfully!");
    } catch (err) {
      showStatus(getApiErrorMessage(err, "Failed to delete record."), "danger");
    } finally {
      setIsProcessing(false);
    }
  };

  const getDuration = (start, end) => {
    if (!start) return "N/A";
    const startDate = new Date(start);
    const endDate = end ? new Date(end) : new Date();

    let years = endDate.getFullYear() - startDate.getFullYear();
    let months = endDate.getMonth() - startDate.getMonth();
    let days = endDate.getDate() - startDate.getDate();

    if (days < 0) {
      months -= 1;
      const prevMonth = new Date(endDate.getFullYear(), endDate.getMonth(), 0);
      days += prevMonth.getDate();
    }
    if (months < 0) {
      years -= 1;
      months += 12;
    }

    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}m`);
    if (days > 0 || parts.length === 0) parts.push(`${days}d`);
    return parts.join(" ");
  };

  const isLoading = isProcessing;

  return (
    <div className="position-relative">
      <div>
        {isLoading && (
          <div className="progress-overlay">
            <div className="spinner-border text-primary"></div>
          </div>
        )}

        <SectionToolbar
          title="Previous employers"
          icon="bi-building"
          statusMsg={statusMsg}
        >
          {editingId !== "new" && (
            <AddButton
              onClick={() => {
                setEditingId("new");
                setFormData({ ...EMPTY_FORM });
              }}
              disabled={isLoading}
              label="Add employer"
            />
          )}
        </SectionToolbar>

        <div className="table-responsive profile-table-wrap">
          <table className="table table-hover align-middle mb-0">
            <thead className="custom-table-header">
              <tr>
                <th className="py-3 ps-4">COMPANY</th>
                <th className="py-3">TYPE</th>
                <th className="py-3">POSITION</th>
                <th className="py-3">DATES</th>
                <th className="py-3">DURATION</th>
                <th className="py-3">REASON</th>
                <th className="py-3 pe-4 text-end col-w-110">ACTIONS</th>
              </tr>
            </thead>
            <tbody className="border-top-0">
              {editingId === "new" && (
                <tr className="bg-primary bg-opacity-10">
                  {renderEditRow(
                    "new",
                    formData,
                    handleChange,
                    handleSave,
                    handleCancel,
                    employeeTypes,
                    isLoading,
                  )}
                </tr>
              )}

              {histories.length > 0
                ? histories.map((h) => (
                    <tr
                      key={h.id}
                      className={
                        editingId === h.id ? "bg-primary bg-opacity-10" : ""
                      }
                    >
                      {editingId === h.id ? (
                        renderEditRow(
                          h.id,
                          formData,
                          handleChange,
                          handleSave,
                          handleCancel,
                          employeeTypes,
                          isLoading,
                        )
                      ) : (
                        <>
                          <td className="ps-4 py-3">
                            <div className="fw-bold text-dark fs-085">
                              {h.companyName}
                            </div>
                            <div className="text-muted fs-065">
                              {h.companyAddress}
                            </div>
                          </td>
                          <td>
                            <span className="badge border bg-light text-dark border-secondary-subtle text-uppercase fs-060">
                              {h.employeeType}
                            </span>
                          </td>
                          <td className="fw-medium fs-080">{h.position}</td>
                          <td className="text-muted fs-075">
                            {h.startDate?.slice(0, 10)} —{" "}
                            {h.endDate ? (
                              h.endDate.slice(0, 10)
                            ) : (
                              <span className="text-primary fw-bold">
                                Present
                              </span>
                            )}
                          </td>
                          <td>
                            <span className="badge rounded-pill bg-primary bg-opacity-10 text-primary border border-primary border-opacity-10 fs-065">
                              {getDuration(h.startDate, h.endDate)}
                            </span>
                          </td>
                          <td className="text-muted text-wrap fs-075 mw-200px">
                            {h.reasonForLeaving || "-"}
                          </td>
                          <td className="pe-4 text-end">
                            <div className="d-flex justify-content-end gap-2">
                              {/* Updated Icons to Match Educational Background */}
                              <button
                                title="Edit"
                                aria-label="Edit"
                                className="btn btn-sm border-0 shadow-none p-0 d-flex align-items-center justify-content-center btn-icon-28"
                                onClick={() => handleEditInitiate(h)}
                                disabled={isLoading}
                              >
                                <i className="bi bi-pencil-square text-primary"></i>
                              </button>
                              <button
                                title="Delete"
                                aria-label="Delete"
                                className="btn btn-sm border-0 shadow-none p-0 d-flex align-items-center justify-content-center btn-icon-28"
                                onClick={() => handleDelete(h.id)}
                                disabled={isLoading}
                              >
                                <i className="bi bi-trash3-fill text-danger"></i>
                              </button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                : editingId !== "new" && (
                    <tr>
                      <td
                        colSpan="7"
                        className="text-center py-5 text-muted small fw-semibold"
                      >
                        No employment history found.
                      </td>
                    </tr>
                  )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const renderEditRow = (
  id,
  formData,
  handleChange,
  handleSave,
  handleCancel,
  types,
  isLoading,
) => (
  <>
    <td className="ps-4 py-3">
      <input
        type="text"
        name="companyName"
        placeholder="Company *"
        value={formData.companyName}
        onChange={handleChange}
        className="form-control mb-1 fw-bold table-field table-field--padded"
        disabled={isLoading}
      />
      <input
        type="text"
        name="companyAddress"
        placeholder="Address"
        value={formData.companyAddress}
        onChange={handleChange}
        className="form-control table-field table-field--padded table-field--sm"
        disabled={isLoading}
      />
    </td>
    <td>
      <select
        name="employeeType"
        value={formData.employeeType || "Permanent"}
        onChange={handleChange}
        className="form-select fw-bold table-field table-field--padded"
        disabled={isLoading}
      >
        {types.map((t) => (
          <option key={t.value} value={t.value}>
            {t.label}
          </option>
        ))}
      </select>
    </td>
    <td>
      <input
        type="text"
        name="position"
        placeholder="Position *"
        value={formData.position}
        onChange={handleChange}
        className="form-control fw-bold table-field table-field--padded"
        disabled={isLoading}
      />
    </td>
    <td>
      <input
        type="date"
        name="startDate"
        value={formData.startDate}
        onChange={handleChange}
        className="form-control mb-1 fw-bold table-field table-field--padded"
        disabled={isLoading}
      />
      <input
        type="date"
        name="endDate"
        value={formData.endDate}
        onChange={handleChange}
        className="form-control fw-bold table-field table-field--padded"
        disabled={isLoading}
      />
    </td>
    <td>
      <span className="text-muted fw-bold fs-065">Calculated</span>
    </td>
    <td>
      <input
        type="text"
        name="reasonForLeaving"
        placeholder="Reason"
        value={formData.reasonForLeaving}
        onChange={handleChange}
        className="form-control fw-bold table-field table-field--padded"
        disabled={isLoading}
      />
    </td>
    <td className="pe-4 text-end">
      <div className="d-flex gap-2 justify-content-end">
        {/* Updated Edit Mode Buttons to Match Educational Background */}
        <button
          title="Save"
          aria-label="Save"
          className="btn btn-success btn-sm p-0 d-flex align-items-center justify-content-center shadow-sm btn-icon-28"
          onClick={() => handleSave(id)}
          disabled={isLoading}
        >
          <i className="bi bi-check-lg"></i>
        </button>
        <button
          title="Cancel"
          aria-label="Cancel"
          className="btn btn-outline-secondary btn-sm p-0 d-flex align-items-center justify-content-center shadow-sm btn-icon-28"
          onClick={handleCancel}
          disabled={isLoading}
        >
          <i className="bi bi-x-lg"></i>
        </button>
      </div>
    </td>
  </>
);

export default EmployeeHistory;
